/**
 * Self-voice matching: which microphone voice in a meeting is the user.
 *
 * The user's voice fingerprint is learned only from meetings that look fully
 * remote, where the mic channel is already known to carry just the user, and
 * never leaves this device. In a shared room the fingerprint picks the user's
 * voice out of the mic's diarized voices, and only that verdict is sent on.
 *
 * The thresholds are uncalibrated starting points for staff dogfooding; the
 * session telemetry reports the scores they should be tuned against.
 */

/** ReDimNet2 B6 (VoxCeleb2, large-margin fine-tune), exported to take a raw 16 kHz waveform. */
export const SELF_VOICE_MODEL_ID = 'redimnet2-b6-vb2-vox2-lm'
export const SELF_VOICE_EMBEDDING_DIM = 192

export const SELF_VOICE_SESSION_CONFIG_EVENT = 'self-voice:session-config'
export const SELF_VOICE_MODEL_EVENT = 'self-voice:model'
export const SELF_VOICE_SESSION_END_EVENT = 'self-voice:session-end'
/** The capture window asks main to score an embedding; the fingerprint itself never leaves main. */
export const SELF_VOICE_SCORE_EVENT = 'self-voice:score'
/** Embeddings one meeting may take, whatever its voices do. Main answers no more scores than this. */
export const MAX_EMBEDDINGS_PER_MEETING = 60
/** The capture window tells main the first time a meeting's mic voice matches the user. */
export const SELF_VOICE_FIRST_MATCH_EVENT = 'self-voice:first-match'
/** GlobalState: the current meeting's `UserVoiceMatches`, which relabel the user's mic voice as them. */
export const USER_VOICE_MATCHES_STATE = 'user-voice-matches'

/**
 * How much of self-voice runs, from the `self-voice-match` flag's variant. Each
 * mode adds to the one before: `fingerprint` learns the user's voice, `match`
 * also finds it in a meeting's mic, `consolidate` also relabels the transcript.
 */
export type SelfVoiceMode = 'off' | 'fingerprint' | 'match' | 'consolidate'

/** A boolean `true` is the flag before it had variants, which did everything. */
export function selfVoiceModeFromFlag(value: boolean | string): SelfVoiceMode {
  if (value === true) return 'consolidate'
  return value === 'fingerprint' || value === 'match' || value === 'consolidate' ? value : 'off'
}

/**
 * Two remote meetings' centroids at least this close are the same speaker. On
 * the prototype's TTS clips one voice scored 0.75-0.81 against itself and
 * 0.28-0.49 against distinct voices; meeting centroids average many samples.
 */
const SAME_SPEAKER_SIMILARITY = 0.65
/** Session centroids kept: the fingerprint follows the user's mics and voice as they change. */
const MAX_SESSIONS = 8
/** Disagreeing remote meetings in a row that, agreeing with each other, replace the fingerprint. */
const REPLACEMENT_SESSIONS = 3
/** Disagreeing remote meetings in a row that suspend the fingerprint until it is re-learned. */
const SUSPENDING_MISMATCHES = 2

/** A mic voice whose mean similarity reaches this is a candidate for the user. */
export const USER_VOICE_SIMILARITY = 0.55
/** A mic voice whose mean similarity stays under this is someone else. */
export const OTHER_VOICE_SIMILARITY = 0.45
/** How far the user's voice must lead the next mic voice. Look-alike voices scored within 0.05 and abstain. */
const USER_VOICE_MARGIN = 0.1
/** Samples a mic voice needs before its verdict counts. */
export const SAMPLES_PER_DECISION = 4
/** Samples after which a voice that still scores between the floors stops being embedded. */
export const MAX_SAMPLES_PER_VOICE = 12
/** Samples of the user's (or the only) mic voice to keep for re-checking the fingerprint. */
export const SAMPLES_FOR_FINGERPRINT = 8

/** What a remote meeting needs before it may teach or re-check the fingerprint. */
const ENROLLMENT_MIN_SYSTEM_SPEECH_MS = 30_000
const ENROLLMENT_MIN_MIC_SPEECH_MS = 30_000
const ENROLLMENT_MIN_DOMINANT_SHARE = 0.9
/** A second mic voice this much of whose speech overlapped the far end is the far end leaking in, not someone in the room. */
const LEAK_ECHO_SHARE = 0.8
/** A clip that overlapped far-end speech more than this may carry the far end leaking in, so it isn't sampled or learned from. */
export const MAX_SAMPLE_ECHO_SHARE = 0.2
const ENROLLMENT_MIN_SAMPLES = 5
export const ENROLLMENT_MIN_CONSISTENCY = 0.6

export type Embedding = ReadonlyArray<number>

export interface SessionCentroid {
  centroid: Array<number>
  atMs: number
}

/** The stored fingerprint: the centroids of the remote meetings it was learned from. */
export interface SelfVoiceStore {
  modelId: string
  sessions: Array<SessionCentroid>
  /** Recent remote meetings that disagreed with `sessions`, oldest first. */
  mismatches: Array<SessionCentroid>
}

export type FingerprintUpdate = 'learned' | 'reinforced' | 'mismatched' | 'replaced'

export interface SelfVoiceSessionConfig {
  mode: SelfVoiceMode
  /** Whether main will score embeddings: false while the fingerprint is still being learned or is suspended, or in `fingerprint` mode. */
  matching: boolean
}

/** A mic voice's evidence over one meeting. Keys are Deepgram's `mic:N`. */
export interface MicVoiceEvidence {
  speechMs: number
  /** Speech that overlapped system audio: crosstalk, or the far end leaking in through speakers. */
  echoMs: number
  embeddings: Array<Array<number>>
  /** For each embedding, the share of its clip that overlapped system audio. */
  echoShares: Array<number>
  /** Similarity of each embedding to the fingerprint, when there was one. */
  scores: Array<number>
}

export interface UserVoiceVerdict {
  /** `mic:N`, or null when no mic voice is clearly the user. */
  speakerKey: string | null
  score: number | null
  margin: number | null
}

/** A verdict for one mic epoch: Deepgram numbers mic voices from 0 on every fresh stream, so `mic:N` means nothing without it. */
export interface MicEpochVerdict extends UserVoiceVerdict {
  micEpoch: number
}

/** What the capture window reports to main when a meeting ends: the mic epoch with the most speech. */
export interface SelfVoiceSessionSummary {
  voices: Record<string, MicVoiceEvidence>
  systemSpeechMs: number
  mergedAudio: boolean
  verdict: UserVoiceVerdict
  embeddingCount: number
  modelLoadMs: number | null
  /** Fresh Deepgram streams the mic went through; each numbers its voices from 0. */
  micEpochs: number
}

/** What the meeting looked like from its audio, for telemetry. Only `remote` meetings teach the fingerprint. */
export type SelfVoiceMeetingType = 'merged-audio' | 'shared-mic' | 'remote' | 'in-person'

export function classifyMeeting(
  audio: Pick<SelfVoiceSessionSummary, 'voices' | 'systemSpeechMs' | 'mergedAudio'>,
): SelfVoiceMeetingType {
  if (audio.mergedAudio) return 'merged-audio'
  if (dominantVoiceShare(audio.voices) < ENROLLMENT_MIN_DOMINANT_SHARE) return 'shared-mic'
  return audio.systemSpeechMs >= ENROLLMENT_MIN_SYSTEM_SPEECH_MS ? 'remote' : 'in-person'
}

/** The first mic voice in a meeting that matched the user. */
export interface SelfVoiceFirstMatch extends MicEpochVerdict {
  /** From the start of matching to the match. */
  elapsedMs: number
  micVoices: number
  meetingType: SelfVoiceMeetingType
}

export function cosineSimilarity(a: Embedding, b: Embedding): number {
  let dot = 0
  let aa = 0
  let bb = 0
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i]
    aa += a[i] * a[i]
    bb += b[i] * b[i]
  }
  return aa === 0 || bb === 0 ? 0 : dot / Math.sqrt(aa * bb)
}

/** The mean of unit-normalized vectors, itself normalized. */
export function averageEmbeddings(vectors: ReadonlyArray<Embedding>): Array<number> {
  const sum = new Array<number>(vectors[0]?.length ?? 0).fill(0)
  for (const vector of vectors) {
    const norm = Math.sqrt(vector.reduce((acc, x) => acc + x * x, 0)) || 1
    vector.forEach((x, i) => (sum[i] += x / norm))
  }
  const norm = Math.sqrt(sum.reduce((acc, x) => acc + x * x, 0)) || 1
  return sum.map((x) => x / norm)
}

function mean(values: ReadonlyArray<number>): number {
  return values.reduce((acc, x) => acc + x, 0) / values.length
}

export function emptySelfVoiceStore(): SelfVoiceStore {
  return { modelId: SELF_VOICE_MODEL_ID, sessions: [], mismatches: [] }
}

/**
 * Every pair of centroids is the same speaker. Not each against their mean:
 * three unrelated voices still sit about 0.58 from their own average.
 */
function centroidsAgree(sessions: ReadonlyArray<SessionCentroid>): boolean {
  return sessions.every((a, i) =>
    sessions.slice(i + 1).every((b) => cosineSimilarity(a.centroid, b.centroid) >= SAME_SPEAKER_SIMILARITY),
  )
}

/**
 * The fingerprint to match with. Two agreeing meetings make it active, so one
 * odd meeting can't define the user; two disagreeing meetings in a row suspend
 * it, since a different person may be using this computer.
 */
export function activeFingerprint(store: SelfVoiceStore): Array<number> | null {
  if (store.sessions.length < 2 || store.mismatches.length >= SUSPENDING_MISMATCHES) return null
  if (!centroidsAgree(store.sessions)) return null
  return averageEmbeddings(store.sessions.map((s) => s.centroid))
}

/**
 * Folds a qualifying remote meeting's centroid into the store. A run of
 * disagreeing meetings only replaces the fingerprint once they agree with each
 * other, so neither a new mic nor a borrowed laptop rewrites it in one meeting.
 */
export function recordRemoteSession(
  store: SelfVoiceStore,
  session: SessionCentroid,
): { store: SelfVoiceStore; update: FingerprintUpdate; similarity: number | null } {
  if (store.sessions.length === 0) {
    return { store: { ...store, sessions: [session], mismatches: [] }, update: 'learned', similarity: null }
  }
  const similarity = cosineSimilarity(session.centroid, averageEmbeddings(store.sessions.map((s) => s.centroid)))
  // Pairwise, as activeFingerprint checks, so a reinforcing meeting can't switch the fingerprint off.
  if (centroidsAgree([...store.sessions, session])) {
    const sessions = [...store.sessions, session].slice(-MAX_SESSIONS)
    const update = store.sessions.length === 1 ? 'learned' : 'reinforced'
    return { store: { ...store, sessions, mismatches: [] }, update, similarity }
  }
  const mismatches = [...store.mismatches, session].slice(-REPLACEMENT_SESSIONS)
  if (mismatches.length === REPLACEMENT_SESSIONS && centroidsAgree(mismatches)) {
    return { store: { ...store, sessions: mismatches, mismatches: [] }, update: 'replaced', similarity }
  }
  return { store: { ...store, mismatches }, update: 'mismatched', similarity }
}

function meanScore(evidence: MicVoiceEvidence): number | null {
  return evidence.scores.length > 0 ? mean(evidence.scores) : null
}

/**
 * The mic voice that is the user: among voices with enough samples to judge,
 * the one that clears the similarity floor and leads the others by the margin.
 * A voice with only a sample or two can't win or block a match.
 */
export function decideUserVoice(voices: Record<string, MicVoiceEvidence>): UserVoiceVerdict {
  const ranked = Object.entries(voices)
    .filter(([, evidence]) => evidence.scores.length >= SAMPLES_PER_DECISION)
    .map(([speakerKey, evidence]) => ({ speakerKey, score: mean(evidence.scores) }))
    .sort((a, b) => b.score - a.score)
  const [best, runnerUp] = ranked
  if (!best || best.score < USER_VOICE_SIMILARITY) {
    return { speakerKey: null, score: best?.score ?? null, margin: null }
  }
  const margin = runnerUp ? best.score - runnerUp.score : null
  if (margin !== null && margin < USER_VOICE_MARGIN) return { speakerKey: null, score: best.score, margin }
  return { speakerKey: best.speakerKey, score: best.score, margin }
}

/** A mic voice has enough samples that say clearly whether it is the user, or more won't help. */
function isVoiceDecided(evidence: MicVoiceEvidence): boolean {
  const score = meanScore(evidence)
  if (score === null || evidence.scores.length < SAMPLES_PER_DECISION) return false
  return (
    score >= USER_VOICE_SIMILARITY || score < OTHER_VOICE_SIMILARITY || evidence.scores.length >= MAX_SAMPLES_PER_VOICE
  )
}

/** Whether another sample of this voice could still change anything. */
export function needsMoreSamples(evidence: MicVoiceEvidence | undefined, hasFingerprint: boolean): boolean {
  if (!evidence) return true
  if (evidence.embeddings.length >= MAX_SAMPLES_PER_VOICE) return false
  if (!hasFingerprint) return evidence.embeddings.length < SAMPLES_FOR_FINGERPRINT
  if (!isVoiceDecided(evidence)) return true
  // The user's voice keeps sampling until there's enough to re-check the fingerprint with.
  const isUser = (meanScore(evidence) ?? 0) >= USER_VOICE_SIMILARITY
  return isUser && evidence.embeddings.length < SAMPLES_FOR_FINGERPRINT
}

export function dominantMicVoice(voices: Record<string, MicVoiceEvidence>): string | null {
  const ranked = Object.entries(voices).sort(([, a], [, b]) => b.speechMs - a.speechMs)
  return ranked[0]?.[0] ?? null
}

/** How alike two mic voices' samples sound, or null when either has none. */
export function voiceSimilarity(a: MicVoiceEvidence, b: MicVoiceEvidence): number | null {
  if (a.embeddings.length === 0 || b.embeddings.length === 0) return null
  return cosineSimilarity(averageEmbeddings(a.embeddings), averageEmbeddings(b.embeddings))
}

/**
 * The dominant mic voice's share of the speech from people in the room. Another
 * mic voice heard almost only over the far end is the far end leaking in, and
 * one that sounds like the dominant voice is Deepgram splitting it, so neither
 * counts. Anyone else counts in full, even if they often talk over the far end.
 */
function dominantVoiceShare(voices: Record<string, MicVoiceEvidence>): number {
  const dominantKey = dominantMicVoice(voices)
  if (!dominantKey) return 1
  const dominant = voices[dominantKey]
  const othersMs = Object.entries(voices)
    .filter(([key]) => key !== dominantKey)
    .reduce((acc, [, voice]) => {
      const similarity = voiceSimilarity(voice, dominant)
      if (similarity !== null && similarity >= SAME_SPEAKER_SIMILARITY) return acc
      const isLeak = voice.speechMs > 0 && voice.echoMs / voice.speechMs >= LEAK_ECHO_SHARE
      return isLeak ? acc : acc + voice.speechMs
    }, 0)
  const roomMs = dominant.speechMs + othersMs
  return roomMs > 0 ? dominant.speechMs / roomMs : 1
}

/**
 * Whether the meeting needs no more embeddings, so the model can be unloaded:
 * every mic voice is decided (when there is a fingerprint), and the voice that
 * would teach or re-check the fingerprint has enough samples.
 */
export function isSessionSettled(voices: Record<string, MicVoiceEvidence>, hasFingerprint: boolean): boolean {
  // A voice that never spoke long enough to embed can't be decided, and mustn't keep the model loaded.
  const keys = Object.keys(voices).filter((key) => voices[key].embeddings.length > 0)
  if (keys.length === 0) return false
  if (hasFingerprint && !keys.every((key) => isVoiceDecided(voices[key]))) return false
  const learningVoice = (hasFingerprint ? decideUserVoice(voices).speakerKey : null) ?? dominantMicVoice(voices)
  return learningVoice !== null && voices[learningVoice].embeddings.length >= SAMPLES_FOR_FINGERPRINT
}

/** The voice's embeddings whose utterances stayed clear of the far end. */
export function cleanSamples(evidence: MicVoiceEvidence): Array<Array<number>> {
  return evidence.embeddings.filter((_, i) => (evidence.echoShares[i] ?? 1) <= MAX_SAMPLE_ECHO_SHARE)
}

export type EnrollmentRejection =
  | 'merged-audio'
  | 'little-system-speech'
  | 'little-mic-speech'
  | 'several-mic-voices'
  | 'echo'
  | 'few-samples'
  | 'inconsistent-samples'

/**
 * The centroid a meeting may teach the fingerprint, or why it may not. Only a
 * meeting that looks fully remote qualifies: the far end on system audio, one
 * voice on the mic, enough of its samples clear of the far end, and those
 * samples agreeing with each other. Samples are judged on their own, since
 * ordinary crosstalk overlaps a good share of any call.
 */
export function enrollmentCentroid(
  summary: SelfVoiceSessionSummary,
): { centroid: Array<number>; consistency: number } | { rejection: EnrollmentRejection } {
  if (summary.mergedAudio) return { rejection: 'merged-audio' }
  if (summary.systemSpeechMs < ENROLLMENT_MIN_SYSTEM_SPEECH_MS) return { rejection: 'little-system-speech' }
  const dominantKey = dominantMicVoice(summary.voices)
  const dominant = dominantKey ? summary.voices[dominantKey] : null
  if (!dominant || dominant.speechMs < ENROLLMENT_MIN_MIC_SPEECH_MS) return { rejection: 'little-mic-speech' }
  if (dominantVoiceShare(summary.voices) < ENROLLMENT_MIN_DOMINANT_SHARE) return { rejection: 'several-mic-voices' }
  if (dominant.embeddings.length < ENROLLMENT_MIN_SAMPLES) return { rejection: 'few-samples' }
  const samples = cleanSamples(dominant)
  if (samples.length < ENROLLMENT_MIN_SAMPLES) return { rejection: 'echo' }
  const centroid = averageEmbeddings(samples)
  const consistency = mean(samples.map((e) => cosineSimilarity(e, centroid)))
  if (consistency < ENROLLMENT_MIN_CONSISTENCY) return { rejection: 'inconsistent-samples' }
  return { centroid, consistency }
}
